import { once } from "@/libs/lodash"
import { derived, writable } from "svelte/store"

const HEARTBEAT_DATA = new ArrayBuffer(0)
const ICE_CANDIDATE_TYPE = "iceCandidate"

const waitICEConnectionCompleted = (
  connection: RTCPeerConnection,
  { timeout }: { timeout?: number } = {},
) =>
  new Promise<void>((resolve) => {
    if (
      connection.iceConnectionState === "completed" ||
      connection.iceGatheringState === "complete"
    ) {
      resolve()
      return
    }

    connection.oniceconnectionstatechange = () =>
      waitICEConnectionCompleted(connection).then(resolve)
    connection.onicegatheringstatechange = () =>
      waitICEConnectionCompleted(connection).then(resolve)

    if (timeout && timeout > 0) setTimeout(resolve, timeout)
  }).then(() => {
    connection.oniceconnectionstatechange = null
    connection.onicegatheringstatechange = null
  })

type DataChannelParams = {
  label: string
  heartbeat?: { interval?: number; retries?: number }
  onOpen?: () => void
  onMessage?: (data: string | ArrayBuffer, channel: RTCDataChannel) => void
  onClose?: () => void
}

const createDataChannel = (
  connection: RTCPeerConnection,
  {
    label,
    onOpen,
    onMessage,
    onClose,
    heartbeat: { interval: heartbeatIntervalTime = 4500, retries: heartbeatMaxRetries = 2 } = {},
  }: DataChannelParams,
) => {
  const channel = connection.createDataChannel(label)

  let heartbeatRetries = 0
  let heartbeatInterval: ReturnType<typeof setInterval> | null = null

  const closeConnection = () => {
    channel.onopen = null
    channel.onmessage = null
    channel.onclose = null
    connection.close()
    onClose?.()

    if (heartbeatInterval) clearInterval(heartbeatInterval)
  }

  channel.onopen = () => {
    heartbeatInterval = setInterval(() => {
      if (heartbeatRetries >= heartbeatMaxRetries || channel.readyState !== "open") {
        closeConnection()
        return
      }

      heartbeatRetries++

      try {
        channel.send(HEARTBEAT_DATA)
      } catch (e) {
        if (channel.readyState === "open") throw e

        closeConnection()
      }
    }, heartbeatIntervalTime)

    onOpen?.()
  }
  channel.onmessage = ({ data }) => {
    heartbeatRetries = 0

    if (data.byteLength === HEARTBEAT_DATA.byteLength) return

    onMessage?.(data, channel)
  }
  channel.onclose = () => closeConnection()

  return channel
}
const sendIceCandidate = (channel: RTCDataChannel, candidate: RTCIceCandidate) =>
  channel.send(JSON.stringify({ type: ICE_CANDIDATE_TYPE, iceCandidate: candidate }))

type SessionConfig = {
  iceCandidates: RTCIceCandidate[]
  sessionDescription: RTCSessionDescriptionInit
}

export const createConnection = (
  config: RTCConfiguration,
  {
    iceTimeout,
    ...params
  }: {
    /**
     * Timeout after which the offer will be created even if not all ICE candidates were collected.
     * There is a case when an iPhone is connected to a Mac through the USB cable and the ICE candidates collection process takes too long.
     * The rest of the ICE candidates are transferred through the data channel.
     */
    iceTimeout?: number
  } & DataChannelParams,
) => {
  const connection = new RTCPeerConnection(config)
  const pendingIceCandidates: RTCIceCandidate[] = []
  let isConnected = false

  const store = writable<{
    localSessionConfig: Partial<SessionConfig> | null
    isConnected: boolean
  }>({ localSessionConfig: null, isConnected })

  const handleClose = once(() => {
    connection.onicecandidate = null
    store.update((s) => ({ ...s, isConnected: (isConnected = false) }))
    params.onClose?.()
  })

  connection.onicecandidate = ({ candidate }) => {
    if (candidate) pendingIceCandidates.push(candidate)
  }
  connection.onconnectionstatechange = () => {
    if (connection.connectionState === "closed") handleClose()
  }

  const channel = createDataChannel(connection, {
    ...params,
    onOpen() {
      store.update((s) => ({ ...s, isConnected: (isConnected = true) }))

      if (pendingIceCandidates.length > 0) {
        pendingIceCandidates.forEach((c) => sendIceCandidate(channel, c))
        pendingIceCandidates.length = 0
      }

      connection.onicecandidate = ({ candidate }) => {
        if (candidate) sendIceCandidate(channel, candidate)
      }

      params.onOpen?.()
    },
    onMessage(data, channel) {
      if (typeof data === "string") {
        const parsed = JSON.parse(data)

        if (parsed.type === ICE_CANDIDATE_TYPE) {
          connection.addIceCandidate(parsed.iceCandidate)
          return
        }
      }

      params.onMessage?.(data, channel)
    },
    onClose: handleClose,
  })

  waitICEConnectionCompleted(connection, { timeout: iceTimeout }).then(() => {
    store.update((s) => ({
      ...s,
      localSessionConfig: { ...s.localSessionConfig, iceCandidates: [...pendingIceCandidates] },
    }))
    pendingIceCandidates.length = 0
  })

  connection.createOffer().then((offer) => {
    store.update((s) => ({
      ...s,
      localSessionConfig: { ...s.localSessionConfig, sessionDescription: offer },
    }))
    connection.setLocalDescription(offer)
  })

  const setRemote = (sessionConfig: SessionConfig) => {
    connection.setRemoteDescription(sessionConfig.sessionDescription)
    sessionConfig.iceCandidates.forEach((c) => connection.addIceCandidate(c))
  }
  const close = () => connection.close()

  return {
    setRemote,
    close,
    ...derived(store, ($store) => {
      const hasLocalSessionConfig = Boolean(
        $store.localSessionConfig?.sessionDescription != null &&
          $store.localSessionConfig.iceCandidates?.length,
      )
      const isWaiting = !$store.isConnected && hasLocalSessionConfig

      return {
        ...$store,
        localSessionConfig: hasLocalSessionConfig
          ? ($store.localSessionConfig as SessionConfig)
          : null,
        isWaiting: isWaiting,
      }
    }),
  }
}
